import { errorToast } from '@/lib/toast'
import axios, {
  AxiosError,
  AxiosProgressEvent,
  AxiosRequestConfig,
} from 'axios'

import {
  IAuthTokens,
  TokenRefreshRequest,
  applyAuthTokenInterceptor,
  clearAuthTokens,
  refreshTokenIfNeeded,
  setAuthTokens,
} from 'axios-jwt'
import { displayError, displayInfo } from './api-error'

export type Method = 'GET' | 'POST' | 'PUT' | 'DELETE'

export interface Route {
  method: Method
  url: string
}

// Server-side rendering (e.g. cast page SSR) cannot resolve relative URLs —
// axios's Node http adapter calls `new URL(baseURL + path)` which throws on
// "/api/...". On dev/prod NEXT_PUBLIC_API_BASE_URL is absolute so this is fine,
// but on local we set it to "/api" (Next.js rewrite proxies to :8080 for the
// browser). For SSR on local, fall back to API_INTERNAL_URL.
function resolveBaseUrl(): string | undefined {
  if (typeof window === 'undefined') {
    return (
      process.env.API_INTERNAL_URL || process.env.NEXT_PUBLIC_API_BASE_URL
    )
  }
  return process.env.NEXT_PUBLIC_API_BASE_URL
}

const axiosInstance = axios.create({
  baseURL: resolveBaseUrl(),
  withCredentials: true,
})

// Module-scope in-memory storage for the access token. The refresh token now
// lives in an HttpOnly cookie set by the API and is never visible to JS.
// Access token is reachable to attach as Authorization: Bearer; lost on hard
// reload and re-bootstrapped via POST /auth/refresh on app mount.
const memoryStorage: Record<string, string> = {}

// Records whether the most recent /auth/refresh failure was TRANSIENT (network
// blip / 5xx / timeout) rather than a genuine session end (HTTP 401). Set at the
// source (requestRefresh) so the classification survives any re-wrapping that
// axios-jwt does to the thrown error before it reaches errorHandler. A genuine
// session end ALWAYS returns 401 (the refresh Passport strategy throws
// UnauthorizedException on an invalid/expired/missing refresh token); anything
// else must NOT log the user out — the session is still valid server-side.
let lastRefreshFailureWasTransient = false

// Classification of the most recent BOOTSTRAP (hard-reload POST /auth/refresh)
// outcome, read by auth.provider's checkAuth to decide the redirect. Middleware
// gates cookieless visitors off protected routes, so a bootstrap failure here is
// never anonymous — it is a genuine session end (HTTP 401) or a transient blip
// (network / 5xx). `expired` drives the calm `?reason=expired` login banner;
// `transient` tells checkAuth NOT to log the user out (the session is still valid
// server-side — SWR / the next reload recovers). Both reset on a successful
// bootstrap/refresh.
let bootstrapSessionExpired = false
let bootstrapSessionTransient = false

export const getBootstrapSessionExpired = (): boolean => bootstrapSessionExpired
export const getBootstrapSessionTransient = (): boolean =>
  bootstrapSessionTransient

const requestRefresh: TokenRefreshRequest = async (): Promise<
  IAuthTokens | string
> => {
  try {
    const response = await axios.post(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/auth/refresh`,
      {},
      { withCredentials: true }
    )

    lastRefreshFailureWasTransient = false
    bootstrapSessionExpired = false
    bootstrapSessionTransient = false
    return {
      accessToken: response.data.accessToken,
      // Refresh token is HttpOnly cookie-only; axios-jwt requires a string here
      // but we never read it back — getStorage().get('refreshToken') is unused.
      refreshToken: 'cookie',
    }
  } catch (e: any) {
    // 401 = genuine session end (sign in again). Anything else (no response,
    // 5xx, timeout, network) = transient — do not wipe tokens or redirect.
    lastRefreshFailureWasTransient = e?.response?.status !== 401
    throw new RefreshTokenError('Refresh Token error')
  }
}

applyAuthTokenInterceptor(axiosInstance, {
  requestRefresh,
  getStorage: () => {
    return {
      get: async (key) => {
        return memoryStorage[key] ?? null
      },
      set: async (key, value) => {
        memoryStorage[key] = value
      },
      remove: async (key) => {
        delete memoryStorage[key]
      },
    }
  },
})

export interface RequestData {
  data?: Record<string, any> | FormData
  headers?: Record<string, string>
  method: Method
  params?: Record<string, string | number>
  options?: {
    noAuth?: boolean
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
    timeout?: number
  }
}

class RefreshTokenError extends Error {
  constructor(message?: string) {
    super(message)
    this.name = 'RefreshTokenError'

    // Make the properties enumerable
    Object.defineProperty(this, 'message', { enumerable: true })
    Object.defineProperty(this, 'name', { enumerable: true })

    // Capture the stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, RefreshTokenError)
    }
  }

  // Optional: Add a toJSON method for better serialization
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      stack: this.stack,
    }
  }
}

/**
 * Bootstrap path for hard reload: directly POST /auth/refresh without going
 * through axios-jwt's `refreshTokenIfNeeded` — that helper requires tokens
 * already in storage and throws "No refresh token available" otherwise. On
 * hard reload the in-memory storage is empty by design; the refresh JWT is
 * in the HttpOnly cookie which the browser auto-attaches via
 * withCredentials. On 200, populate axios-jwt storage so subsequent calls
 * use the standard interceptor path. On 401 (no cookie or expired), resolve
 * silently — checkAuth in auth.provider handles the redirect.
 *
 * Deduplication: the API rotates the refresh-token hash on every successful
 * /auth/refresh, making the in-flight cookie single-use. If two callers
 * (e.g. auth.provider's mount useEffect + the first SWR fetch's
 * getOrRefreshToken fallback) fire bootstrap concurrently, the second
 * one races with the first's rotation and lands on a stale cookie → 401
 * → eventual disconnect-redirect. A module-level cached promise dedupes
 * concurrent callers onto ONE in-flight attempt. On a settled FAILURE the
 * promise is released so a later request/reload can retry (a failed refresh
 * rotated nothing → no double-rotation race); a settled SUCCESS stays cached
 * so subsequent callers skip re-bootstrap, preserving the anti-rotation dedupe.
 */
let bootstrapPromise: Promise<boolean> | null = null

export const bootstrapAccessToken = async (): Promise<boolean> => {
  if (bootstrapPromise) return bootstrapPromise
  bootstrapPromise = (async () => {
    let ok = false
    try {
      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true }
      )
      if (response.data?.accessToken) {
        setAuthTokens({
          accessToken: response.data.accessToken,
          refreshToken: 'cookie',
        })
        ok = true
        bootstrapSessionExpired = false
        bootstrapSessionTransient = false
        lastRefreshFailureWasTransient = false
      } else {
        // 200 without a token is an unexpected shape — treat as transient so we
        // neither log the user out nor cache a permanent failure.
        bootstrapSessionExpired = false
        bootstrapSessionTransient = true
        lastRefreshFailureWasTransient = true
      }
    } catch (e: any) {
      // 401 = genuine session end (sign in again). Anything else (no response,
      // 5xx, timeout, network) = transient — do NOT log the user out.
      const isGenuine401 = e?.response?.status === 401
      bootstrapSessionExpired = isGenuine401
      bootstrapSessionTransient = !isGenuine401
      lastRefreshFailureWasTransient = !isGenuine401
    } finally {
      // Release the cached promise on a settled FAILURE so a later request / SWR
      // revalidation can attempt a FRESH bootstrap without a full page reload.
      // A failed /auth/refresh performed no rotation, so this cannot race the
      // S1.2 double-rotation the dedupe guards. Concurrent in-flight callers
      // already share this promise reference; only future callers see the reset.
      if (!ok) bootstrapPromise = null
    }
    return ok
  })()
  return bootstrapPromise
}

let tokenPromise: Promise<string | undefined> | undefined

// axios-jwt's `refreshTokenIfNeeded` requires tokens already in storage and
// throws "No refresh token available" otherwise. On hard reload that's our
// expected state — the refresh JWT lives in an HttpOnly cookie. Fall back to
// the direct bootstrap path on first call: it POSTs /auth/refresh with the
// cookie, populates storage on success, and lets the standard interceptor
// take over from there.
const tryRefreshOrBootstrap = async (): Promise<string | undefined> => {
  try {
    return await refreshTokenIfNeeded(requestRefresh)
  } catch (e: any) {
    // axios-jwt throws "No refresh token available" when storage is empty.
    // On hard reload that's our expected state — try the direct cookie path.
    const ok = await bootstrapAccessToken()
    if (ok) return refreshTokenIfNeeded(requestRefresh)
    // Bootstrap failed. Middleware gates cookieless visitors off protected
    // routes, so this is NEVER anonymous — it is a genuine session end (401) or
    // a transient blip (network / 5xx). Throw so the calm errorHandler
    // classifies it (genuine → neutral notice + clear + ?reason=expired;
    // transient → neutral notice, no logout, SWR retries) instead of firing a
    // doomed no-auth request that would 401 and spawn a red "Error fetching" toast.
    throw new RefreshTokenError('Refresh Token error')
  }
}

export const getOrRefreshToken = async (): Promise<string | undefined> => {
  // Store the existing promise first
  const existingPromise = tokenPromise
  if (existingPromise) {
    return existingPromise
  }

  // Create the new promise first, then assign it atomically
  const newPromise = tryRefreshOrBootstrap().catch((error) => {
    // Only clear the tokenPromise if it's still our promise
    if (tokenPromise === newPromise) {
      tokenPromise = undefined
    }
    throw new RefreshTokenError('Refresh Token error: ' + error)
  })

  // Atomic assignment of the new promise
  tokenPromise = newPromise
  return newPromise
}

// A session ending (refresh token expired / invalidated) is an EXPECTED event,
// not an application error. `isRefreshTokenError` identifies that path so it can
// be surfaced as a neutral "please sign in again" notice rather than a red error
// toast (and so the secondary fetcher toast can be suppressed). The typeof guard
// is safer than a bare `error.message.includes` when `message` is undefined.
export const isRefreshTokenError = (error: any): boolean =>
  error instanceof RefreshTokenError ||
  (typeof error?.message === 'string' &&
    error.message.includes('Refresh Token error'))

let toastPromise: Promise<void> | null = null

const showDisconnectedToast = async () => {
  if (!toastPromise) {
    toastPromise = new Promise((resolve) => {
      // Neutral, expected-event notice — not a red error.
      displayInfo('Session expirée', 'Veuillez vous reconnecter.')
      setTimeout(() => {
        toastPromise = null
        resolve()
      }, 5000)
    })
    return toastPromise
  }
  return toastPromise
}

// Transient (network/5xx) refresh failure: the session is still valid, so we do
// NOT log the user out — we show a neutral, reassuring notice and let SWR retry
// (it revalidates on reconnect/focus). Deduped + non-blocking so a burst of
// concurrent SWR fetches can't spam it and it never delays the request chain.
let transientToastShown = false

const showTransientToast = () => {
  if (transientToastShown) return
  transientToastShown = true
  displayInfo('Connexion interrompue', 'Reconnexion en cours…')
  setTimeout(() => {
    transientToastShown = false
  }, 5000)
}

const apiRequest = async <T>(
  route: string,
  request: RequestData
): Promise<T> => {
  const isFileUpload = request.data instanceof FormData

  const options: AxiosRequestConfig = {
    data: request.data,
    params: request.params ?? {},
    method: request.method,
    url: route,
    headers: isFileUpload
      ? { 'Content-Type': 'multipart/form-data', ...request.headers }
      : { 'Content-Type': 'application/json', ...request.headers },
    onUploadProgress: request.options?.onUploadProgress,
    ...(request.options?.timeout && { timeout: request.options.timeout }),
  }
  let response
  try {
    if (!request.options?.noAuth) {
      await getOrRefreshToken()
    }
    response = await axiosInstance.request(options)
  } catch (err: any) {
    // A classified session expiry / transient refresh failure is an EXPECTED
    // event handled by errorHandler — don't emit an app-level console.error for
    // it (the browser's own 401 network line is unavoidable). Real errors log.
    if (!isRefreshTokenError(err)) {
      console.error(JSON.stringify(err, null, 2))
    }
    await errorHandler(err)
  }
  return response?.data
}

export default apiRequest

const errorHandler = async (error: any) => {
  if (isRefreshTokenError(error)) {
    if (lastRefreshFailureWasTransient) {
      // Transient /auth/refresh failure (network/5xx): the session is still
      // valid — surface a neutral notice and let SWR retry. Do NOT wipe tokens
      // or redirect (that would be a false logout on a momentary glitch).
      showTransientToast()
    } else {
      await showDisconnectedToast()
      clearAuthTokens()
      if (typeof window !== 'undefined') {
        // `?reason=expired` lets the login page render a calm "session expired"
        // banner instead of the user landing on a bare login form with no context.
        window.location.href = '/auth/login?reason=expired'
      }
    }
  }
  const serverMessage = error?.response?.data?.message
  if (serverMessage) {
    error.message = Array.isArray(serverMessage)
      ? serverMessage.join(', ')
      : serverMessage
  }
  throw error
}

export const fetcher = async <T>([url, params, options]: any[]): Promise<T> => {
  try {
    const response = await apiRequest(url, { method: 'GET', params, options })
    return response as T
  } catch (error: any) {
    // Session expiry / transient refresh failure is expected + already handled by
    // errorHandler — suppress BOTH the console.error and the secondary red
    // "Error fetching …" toast so it reads as a clean re-login, not an error.
    if (!isRefreshTokenError(error)) {
      console.error('Failed to fetch:', error)
    }
    if (error.response?.status !== 404 && !isRefreshTokenError(error)) {
      displayError(`Error fetching ${url}`, error.message)
    }
    throw error
  }
}
